import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import App from './App';
import './index.css';
import { ReactQueryProvider } from './providers/ReactQueryProvider';
import { MediaSettingsProvider } from './providers/MediaSettingsProvider';
import { defaultImageService } from './services/defaultImageService';

// Determine environment
const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_SENTRY_ENVIRONMENT === 'dev';

// Initialize Sentry
Sentry.init({
  dsn: "https://ff8c7bf46e01f9504d01432420c2f4e4@o4509554889654272.ingest.us.sentry.io/4509554892341248",
  environment: isDevelopment ? 'development' : 'production',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring - conservative rates to prevent rate limiting
  tracesSampleRate: isDevelopment ? 0.01 : 0.1, // 1% in dev, 10% in prod
  // Session Replay - disabled in development to prevent excessive data
  replaysSessionSampleRate: isDevelopment ? 0.0 : 0.1, // Disabled in dev, 10% in prod
  replaysOnErrorSampleRate: isDevelopment ? 0.1 : 1.0, // Only error replays in dev
  
  beforeSend(event) {
    // In development mode, only send errors if toggle is enabled
    if (isDevelopment) {
      console.log('Sentry event captured (dev mode):', event);
      
      // Check if dev error sending is enabled via environment variable
      const shouldSendDevErrors = import.meta.env.VITE_SENTRY_SEND_DEV_ERRORS === 'true';
      
      if (shouldSendDevErrors) {
        console.log('🚀 Sending dev error to Sentry dashboard (toggle enabled)...');
        return event;
      } else {
        console.log('🚫 Dev error not sent to Sentry (toggle disabled)');
        return null;
      }
    }
    return event;
  },
});

// Initialize default image cache on app startup
defaultImageService.initializeCache().catch(error => {
  console.log('Failed to initialize default image cache on startup:', error);
});

createRoot(document.getElementById("root")!).render(
  <ReactQueryProvider>
    <MediaSettingsProvider>
      <App />
    </MediaSettingsProvider>
  </ReactQueryProvider>
);
